import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import AuditPopover from '../components/AuditPopover';
import QuickListManager from '../components/QuickListManager';
import DataTable from '../components/DataTable';
import { Button, Modal, FormField, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero, CardSplitButton } from '../components/card';
import { TAB_ICONS } from '../utils/tabIcons';

const emptyForm = {
  transaction_date: new Date().toISOString().substring(0, 10),
  description: '', amount: '', reference_no: '', notes: '',
  register_id: '',
  personnel_id: '', special_code1: '', special_code2: '',
};

function fmt(n, cur) {
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0) + (cur ? ' ' + cur : '');
}
function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('tr-TR');
}

// modalMode: null | 'view' | 'edit' | 'add'
export default function CashOpeningPage() {
  const [transactions, setTransactions] = useState([]);
  const [registers, setRegisters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', register_id: '' });

  const [modalMode, setModalMode] = useState(null);
  const [selectedTx, setSelectedTx] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [settings, setSettings] = useState({});
  const [personnel, setPersonnel] = useState([]);
  const [specialCodes1, setSpecialCodes1] = useState([]);
  const [specialCodes2, setSpecialCodes2] = useState([]);
  const [showQuickList, setShowQuickList] = useState(null);

  const fetchSpecialCodes = useCallback(async () => {
    try {
      const r = await api.get('/invoices/lookups/all');
      if (r.data.success) {
        setSpecialCodes1(r.data.data?.specialCodes1 || []);
        setSpecialCodes2(r.data.data?.specialCodes2 || []);
      }
    } catch {}
  }, []);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: 'opening' };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/cash-registers/all-transactions', { params });
      if (res.data.success) setTransactions(res.data.data || []);
    } catch (err) {
      console.error('cash opening error:', err.response?.data || err.message);
    } finally { setLoading(false); }
  }, [filters]);

  const fetchRegisters = useCallback(async () => {
    try {
      const res = await api.get('/cash-registers');
      if (res.data.success) setRegisters((res.data.data.items || []).filter(r => r.is_active));
    } catch {}
  }, []);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);
  useEffect(() => { fetchRegisters(); }, [fetchRegisters]);
  useEffect(() => {
    api.get('/settings').then(r => { if (r.data.success) setSettings(r.data.data); }).catch(() => {});
    api.get('/personnel', { params: { limit: 200 } }).then(r => {
      if (r.data.success) setPersonnel((r.data.data.items || []).filter(p => p.is_active));
    }).catch(() => {});
  }, []);
  useEffect(() => { fetchSpecialCodes(); }, [fetchSpecialCodes]);

  const closeModal = () => { setModalMode(null); setSelectedTx(null); setMsg(null); };

  const openAdd = () => {
    const defId = settings.default_cash_register_id && registers.find(r => r.id === settings.default_cash_register_id)
      ? settings.default_cash_register_id
      : (registers[0]?.id || '');
    setForm({ ...emptyForm, register_id: defId });
    setSelectedTx(null);
    setMsg(null);
    setModalMode('add');
  };

  const openView = (tx) => {
    setForm({
      transaction_date: tx.transaction_date ? new Date(tx.transaction_date).toISOString().substring(0, 10) : emptyForm.transaction_date,
      description: tx.description || '',
      amount: String(tx.amount || ''),
      reference_no: tx.reference_no || '',
      notes: tx.notes || '',
      register_id: tx.register_id,
    });
    setSelectedTx(tx);
    setMsg(null);
    setModalMode('view');
  };

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    const { register_id, transaction_date, description, amount } = form;
    if (!register_id) return setMsg({ type: 'error', text: 'Kasa secin.' });
    if (!transaction_date || !description || !amount) return setMsg({ type: 'error', text: 'Tarih, aciklama ve tutar zorunludur.' });
    setSaving(true); setMsg(null);
    try {
      const body = {
        transaction_date, description,
        transaction_type: 'opening',
        amount: parseFloat(amount),
        reference_no: form.reference_no || null,
        notes: form.notes || null,
        personnel_id: form.personnel_id || null,
        special_code1: form.special_code1 || null,
        special_code2: form.special_code2 || null,
      };
      if (modalMode === 'edit') {
        await api.put('/cash-registers/' + register_id + '/transactions/' + selectedTx.id, body);
      } else {
        await api.post('/cash-registers/' + register_id + '/transactions', body);
      }
      closeModal();
      await fetchTransactions();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi.' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedTx) return;
    if (!window.confirm('Bu acilis fisini silmek istiyor musunuz?')) return;
    try {
      await api.delete('/cash-registers/' + selectedTx.register_id + '/transactions/' + selectedTx.id);
      closeModal();
      fetchTransactions();
    } catch (err) {
      alert(err.response?.data?.message || 'Silinemedi.');
    }
  };

  const tf = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const ro = modalMode === 'view';

  const filtered = transactions.filter(tx =>
    !search ||
    tx.description?.toLowerCase().includes(search.toLowerCase()) ||
    tx.register_name?.toLowerCase().includes(search.toLowerCase()) ||
    (tx.reference_no || '').toLowerCase().includes(search.toLowerCase())
  );

  const sourceRegister = registers.find(r => r.id === form.register_id);

  // Modal aksiyonları — footer'a taşındı (yeni Modal API)
  const inEdit = modalMode === 'edit' || modalMode === 'add';
  const inView = modalMode === 'view';
  const isNew = modalMode === 'add';

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Kasa Açılış Fişleri"
        count={filtered.length}
        unit="kayit"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Ara..."
        onRefresh={fetchTransactions}
        refreshing={loading}
        actions={<Button size="sm" onClick={openAdd}>+ Yeni Açılış Fisi</Button>}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'date_from', type: 'date', label: 'İlk Tarih' },
          { key: 'date_to', type: 'date', label: 'Son Tarih' },
          { key: 'register_id', type: 'select', label: 'Kasa', flex: '1 1 220px', minWidth: 200,
            options: [{ value: '', label: 'Tüm Kasalar' }, ...registers.map(r => ({ value: r.id, label: `${r.register_name} (${r.currency_code})` }))] },
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="cash-opening-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Kayıt bulunamadı"
          data={filtered}
          rowKey="id"
          onRowClick={tx => openView(tx)}
          columns={[
            { id: 'doc_no', header: 'Belge No', accessor: 'document_no', sortable: true, width: 130,
              cell: tx => <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: 'var(--accent-primary)', whiteSpace: 'nowrap' }}>{tx.document_no || '—'}</span> },
            { id: 'date', header: 'Tarih', accessor: 'transaction_date', sortable: true, width: 110,
              sortFn: (a,b) => new Date(a.transaction_date||0)-new Date(b.transaction_date||0),
              cell: tx => <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(tx.transaction_date)}</span> },
            { id: 'register', header: 'Kasa', accessor: 'register_name', sortable: true, width: 150,
              cell: tx => <span style={{ fontWeight: 600 }}>{tx.register_name}</span> },
            { id: 'description', header: 'Açıklama', accessor: 'description', sortable: true,
              cell: tx => <><div>{tx.description}</div>{tx.notes && <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>{tx.notes}</div>}</> },
            { id: 'ref_no', header: 'Ref No', accessor: 'reference_no', sortable: true, width: 130,
              cell: tx => <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>{tx.reference_no || '—'}</span> },
            { id: 'amount', header: 'Tutar', accessor: tx => parseFloat(tx.amount)||0, sortable: true, width: 140,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontWeight: 700, color: '#7c3aed', whiteSpace: 'nowrap' },
              cell: tx => `+${fmt(tx.amount, tx.currency_code)}` },
            { id: 'audit', header: '', sortable: false, hideable: false, draggable: false, filterable: false, width: 32,
              cellStyle: { textAlign: 'center' },
              cell: tx => <span onClick={e => e.stopPropagation()}><AuditPopover endpoint={`/cash-registers/transactions/${tx.id}/audit`} /></span> },
          ]}
        />
      </div>

      {modalMode !== null && (
        <Modal
          icon={null}
          title={modalMode === 'add' ? 'Yeni Kasa Açılış Fişi' : 'Kasa Açılış Fişi'}
          size="md"
          onClose={closeModal}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {inView && <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {inEdit && <Button variant="ghost" onClick={() => { if (isNew) closeModal(); else { setMsg(null); setModalMode('view'); } }}>{isNew ? 'Vazgeç' : 'İptal'}</Button>}
                {inEdit && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => handleSave('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: () => handleSave('new') },
                      { label: 'Kaydet ve Kapat',  icon: '✓', onClick: () => handleSave('close') },
                    ]}
                  />
                )}
                {inView && <Button onClick={() => { setMsg(null); setModalMode('edit'); }}>✎ Düzenle</Button>}
              </div>
            </>
          }
        >
          {modalMode !== 'add' && selectedTx && (
            <CardHero
              icon="💵"
              code={selectedTx.document_no}
              title="Kasa Açılış"
              subtitle={selectedTx.register_name}
              kpis={[
                selectedTx.amount != null && {
                  label: 'Tutar', value: fmt(selectedTx.amount, selectedTx.currency_code),
                  color: 'success', sublabel: selectedTx.currency_code,
                },
              ].filter(Boolean)}
            />
          )}
          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <FormField label="Belge No">
              <input
                disabled
                value={modalMode === 'add' ? 'Otomatik' : (selectedTx?.document_no || '—')}
                style={{ fontFamily: 'monospace', opacity: 0.7 }}
              />
            </FormField>
            <div />

            <FormField label="Kasa" required={!ro}>
              {(ro || modalMode === 'edit') ? (
                <input disabled value={selectedTx?.register_name || '—'} />
              ) : (
                <select value={form.register_id} onChange={e => tf('register_id', e.target.value)}>
                  <option value="">Kasa secin</option>
                  {registers.map(r => <option key={r.id} value={r.id}>{r.register_name} ({r.currency_code})</option>)}
                </select>
              )}
            </FormField>

            <FormField label="Tarih" required={!ro}>
              <input
                type={ro ? 'text' : 'date'}
                disabled={ro}
                value={ro ? fmtDate(form.transaction_date) : form.transaction_date}
                onChange={e => tf('transaction_date', e.target.value)}
              />
            </FormField>

            <FormField label={`Açılış Tutari${!ro && sourceRegister ? ' (' + sourceRegister.currency_code + ')' : ''}`} required={!ro}>
              <input
                type={ro ? 'text' : 'number'} min="0" step="0.01"
                disabled={ro}
                placeholder={ro ? '' : '0.00'}
                value={ro ? fmt(form.amount, selectedTx?.currency_code) : form.amount}
                onChange={e => tf('amount', e.target.value)}
              />
            </FormField>

            <FormField label="Referans No">
              <input disabled={ro} value={form.reference_no} onChange={e => tf('reference_no', e.target.value)} placeholder={ro ? '' : 'Opsiyonel'} />
            </FormField>

            <FormField label="Açıklama" required={!ro} fullWidth>
              <input disabled={ro} value={form.description} onChange={e => tf('description', e.target.value)} placeholder={ro ? '' : 'Açılış aciklamasi'} />
            </FormField>

            <FormField label="Not" fullWidth>
              <input disabled={ro} value={form.notes} onChange={e => tf('notes', e.target.value)} placeholder={ro ? '' : 'Opsiyonel'} />
            </FormField>

            <FormField label="Personel">
              <select value={form.personnel_id || ''} onChange={e => tf('personnel_id', e.target.value)} disabled={ro}>
                <option value="">Seçiniz</option>
                {personnel.map(p => <option key={p.id} value={p.id}>{p.full_name || (p.first_name + ' ' + p.last_name)}</option>)}
              </select>
            </FormField>
            <div />

            <FormField label={<>Özel Kod 1 {!ro && <button type="button" className="quick-add-btn" onClick={() => setShowQuickList('special1')}>+</button>}</>}>
              <select disabled={ro} value={form.special_code1 || ''} onChange={e => tf('special_code1', e.target.value)}>
                <option value="">Seçiniz</option>
                {specialCodes1.map(s => <option key={s.id} value={s.code || s.name}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
              </select>
            </FormField>
            <FormField label={<>Özel Kod 2 {!ro && <button type="button" className="quick-add-btn" onClick={() => setShowQuickList('special2')}>+</button>}</>}>
              <select disabled={ro} value={form.special_code2 || ''} onChange={e => tf('special_code2', e.target.value)}>
                <option value="">Seçiniz</option>
                {specialCodes2.map(s => <option key={s.id} value={s.code || s.name}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
              </select>
            </FormField>
          </div>
        </Modal>
      )}

      {showQuickList && (
        <QuickListManager
          open={true}
          title={showQuickList === 'special1' ? 'Özel Kod 1' : 'Özel Kod 2'}
          items={showQuickList === 'special1' ? specialCodes1 : specialCodes2}
          apiBase="/invoices/special-codes"
          extraBody={{ code_type: showQuickList }}
          onClose={() => setShowQuickList(null)}
          onChanged={fetchSpecialCodes}
        />
      )}
    </div>
  );
}
